import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { SnapshotMeta, SnapshotDiff } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

/**
 * Draft Comparison — labelled whole-manuscript snapshots and a story-level diff
 * (scenes added/removed/changed, word + entity deltas). Deterministic; snapshots
 * are captured on demand, never on autosave.
 */
export function DraftsPanel({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const [snaps, setSnaps] = useState<SnapshotMeta[]>([]);
  const [label, setLabel] = useState("");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { snapshots } = await api.snapshots(projectId);
      setSnaps(snapshots);
      if (snapshots.length >= 2) { setA((x) => x || snapshots[1]!.id); setB((x) => x || snapshots[0]!.id); }
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't load snapshots"); }
    finally { setLoading(false); }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const snapshot = async () => {
    const l = label.trim();
    if (!l) return;
    setBusy(true);
    try { await api.createSnapshot(projectId, l); setLabel(""); await load(); toast.success(`Snapshot "${l}" saved.`); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't snapshot"); }
    finally { setBusy(false); }
  };

  const compare = async () => {
    if (!a || !b || a === b) { toast.info("Pick two different snapshots."); return; }
    setBusy(true);
    try { setDiff(await api.compareSnapshots(projectId, a, b)); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Compare failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    try { await api.deleteSnapshot(projectId, id); setDiff(null); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Delete failed"); }
  };

  if (loading) return <div className="insights-loading"><Icon name="history" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;

  const delta = (n: number) => n === 0 ? "±0" : n > 0 ? `+${n}` : `${n}`;

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head"><Icon name="history" size={16} style={{ color: "var(--accent)" }} /><h3>Draft snapshots</h3></div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
          Capture a labelled snapshot of the whole manuscript, then compare two to see what changed at the story level. Snapshots are on-demand (never on autosave).
        </p>
        <div className="revmode-add">
          <input className="input" placeholder="Snapshot label (e.g. Draft 2)" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void snapshot(); }} />
          <button className="btn btn-sm btn-primary" onClick={() => void snapshot()} disabled={busy || !label.trim()}><Icon name="plus" size={13} /> Snapshot now</button>
        </div>
        {snaps.length === 0 ? (
          <p className="muted insight-empty">No snapshots yet. Capture one to start tracking drafts.</p>
        ) : (
          <ul className="canon-list" style={{ marginTop: 10 }}>
            {snaps.map((s) => (
              <li key={s.id} className="canon-item">
                <div className="grow"><span className="strong">{s.label}</span> <span className="faint" style={{ fontSize: 11 }}>· {formatNumber(s.words)} words · {s.sceneCount} scenes · {new Date(s.createdAt).toLocaleString()}</span></div>
                <button className="btn btn-icon btn-ghost" title="Delete" onClick={() => void remove(s.id)}><Icon name="trash" size={13} /></button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {snaps.length >= 2 && (
        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="layers" size={16} style={{ color: "var(--accent)" }} /><h3>Compare</h3></div>
          <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <select className="select" value={a} onChange={(e) => setA(e.target.value)} style={{ maxWidth: 220 }}>
              {snaps.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <Icon name="chevronRight" size={14} className="faint" />
            <select className="select" value={b} onChange={(e) => setB(e.target.value)} style={{ maxWidth: 220 }}>
              {snaps.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button className="btn btn-sm btn-primary" onClick={() => void compare()} disabled={busy}>Compare</button>
          </div>

          {diff && (
            <div style={{ marginTop: 14 }}>
              <div className="polish-metrics">
                <div className="polish-metric"><div className="polish-metric-value">{delta(diff.totals.words.delta)}</div><div className="polish-metric-label">words</div></div>
                <div className="polish-metric"><div className="polish-metric-value">{delta(diff.totals.scenes.delta)}</div><div className="polish-metric-label">scenes</div></div>
                <div className="polish-metric"><div className="polish-metric-value">{delta(diff.totals.characters)}</div><div className="polish-metric-label">characters</div></div>
                <div className="polish-metric"><div className="polish-metric-value">{delta(diff.totals.threads)}</div><div className="polish-metric-label">plot threads</div></div>
              </div>
              <DiffList label="Scenes added" cls="insight-ok" items={diff.scenesAdded.map((s) => ({ id: s.id, text: `${s.chapterTitle} / ${s.title}`, extra: `+${s.words}w` }))} onOpenScene={onOpenScene} />
              <DiffList label="Scenes removed" cls="insight-warn" items={diff.scenesRemoved.map((s) => ({ id: s.id, text: `${s.chapterTitle} / ${s.title}`, extra: `−${s.words}w`, noLink: true }))} onOpenScene={onOpenScene} />
              <DiffList label="Scenes changed" cls="" items={diff.scenesChanged.map((s) => ({ id: s.id, text: `${s.chapterTitle} / ${s.title}`, extra: `${s.wordsBefore}→${s.wordsAfter}w` }))} onOpenScene={onOpenScene} />
              {diff.scenesAdded.length + diff.scenesRemoved.length + diff.scenesChanged.length === 0 && <p className="faint" style={{ fontSize: 12, marginTop: 8 }}>No scene-level changes between these snapshots.</p>}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function DiffList({ label, cls, items, onOpenScene }: { label: string; cls: string; items: { id: string; text: string; extra: string; noLink?: boolean }[]; onOpenScene: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="insight-card-head" style={{ marginBottom: 6 }}><span className={`chip ${cls}`}>{items.length}</span><strong style={{ fontSize: 13 }}>{label}</strong></div>
      <ul className="canon-list">
        {items.map((it) => (
          <li key={it.id} className="canon-item">
            {it.noLink ? <span className="grow">{it.text}</span> : <button className="insight-link grow" style={{ textAlign: "left" }} onClick={() => onOpenScene(it.id)}>{it.text}</button>}
            <span className="faint" style={{ fontSize: 11 }}>{it.extra}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
